import type { StaffingFilterStatus } from '@projektverwaltung/shared';
import { useTranslation } from 'react-i18next';
import type { FilterableItem } from '../../../lib/project-filters.js';
import type { UseProjectFiltersResult } from '../../../lib/use-project-filters.js';
import {
  Chip,
  ChipRow,
  ChipSelect,
  MultiSelectPicker,
  PersonPicker,
  SearchInput,
} from './filter-primitives.js';

/**
 * Filterleiste für Projektlisten (Staffing-Tab, Worklist-Tab — #114). Status nur im
 * Staffing-Tab: seit #158 der berechnete Positionsstatus — ein Projekt passt, wenn eine seiner
 * Positionen diesen Status hat (#163: `zu_bestaetigen` = mindestens ein KI-Vorschlag). Die Worklist
 * lädt keine Positionen.
 */
const STATUS_WERTE: StaffingFilterStatus[] = [
  'zu_bestaetigen',
  'offen',
  'vorgeschlagen',
  'float',
  'erledigt',
];

export function ProjectFilterBar<T extends FilterableItem>({
  filters,
  showStatus,
}: {
  filters: UseProjectFiltersResult<T>;
  showStatus: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-xl border p-3 space-y-2"
      style={{ background: 'var(--surface)', borderColor: 'var(--line-soft)' }}
    >
      <ChipSelect
        label={t('v2.staffing.filter_segment', 'Segment')}
        value={filters.state.segment || null}
        onChange={(v) => filters.setSegment(v ?? '')}
        allLabel={t('v2.staffing.filter_all', 'Alle')}
        options={filters.options.segment}
      />
      {showStatus && (
        <ChipSelect
          label={t('v2.staffing.filter_status', 'Status')}
          value={filters.status === 'all' ? null : filters.status}
          onChange={(v) => filters.setStatus((v ?? 'all') as 'all' | StaffingFilterStatus)}
          allLabel={t('v2.staffing.filter_all', 'Alle')}
          options={STATUS_WERTE.map((s) => ({
            value: s,
            label: t(`v2.staffing_maske.status.${s}`, s),
          }))}
        />
      )}
      {/* #55: Personen-, Tag- und Projektnummer-Filter wie im Phase Board.
          Die Picker kommen aus filter-primitives.tsx — dieselbe Komponente,
          die auch das Phase Board nutzt (dort seit #55 importiert statt lokal). */}
      <MultiSelectPicker
        options={filters.options.pl}
        value={filters.state.pl}
        onToggle={filters.togglePl}
        onClear={filters.clearPl}
        label={t('v2.staffing.filter_pl', 'Projektleiter')}
        placeholder={t('v2.staffing.pl_pick', 'Projektleiter wählen…')}
        searchPlaceholder={t('v2.staffing.pl_search', 'Projektleiter suchen…')}
        clearLabel={t('v2.staffing.pl_clear', 'Alle entfernen')}
        countLabel={t('v2.staffing.pl_count', 'ausgewählt')}
        noMatchLabel={t('v2.detail.assign_no_match', 'Keine Treffer.')}
      />
      <PersonPicker
        options={filters.options.manager}
        value={filters.state.manager}
        onChange={filters.setManager}
        label={t('v2.staffing.filter_manager', 'Manager')}
        placeholder={t('v2.staffing.manager_pick', 'Person wählen…')}
        searchPlaceholder={t('v2.staffing.manager_search', 'Manager suchen…')}
        clearLabel={t('v2.staffing.manager_clear', 'Filter entfernen')}
        noMatchLabel={t('v2.detail.assign_no_match', 'Keine Treffer.')}
      />
      <MultiSelectPicker
        options={filters.options.tags}
        value={filters.state.tags}
        onToggle={filters.toggleTag}
        onClear={filters.clearTags}
        label={t('v2.staffing.filter_tag', 'Tag')}
        placeholder={t('v2.staffing.tag_pick', 'Tags wählen…')}
        searchPlaceholder={t('v2.staffing.tag_search', 'Tag suchen…')}
        clearLabel={t('v2.staffing.tag_clear', 'Alle entfernen')}
        countLabel={t('v2.staffing.tag_count', 'ausgewählt')}
        noMatchLabel={t('v2.detail.assign_no_match', 'Keine Treffer.')}
      />
      <SearchInput
        value={filters.state.search}
        onChange={filters.setSearch}
        placeholder={t('v2.staffing.search_placeholder', 'Projektnummer, Kunde oder Projekt…')}
      />
      {/* #56: Sortierfeld als Chips, Richtung als eigener Umschalter — bewusst
          getrennt, damit ein Richtungswechsel das gewählte Feld nicht verliert. */}
      <ChipRow label={t('v2.staffing.sort', 'Sortierung')}>
        <Chip
          active={filters.state.sortField === 'kl_followup_date'}
          onClick={() => filters.setSortField('kl_followup_date')}
        >
          {t('v2.staffing.sort_followup', 'Wieder fällig am')}
        </Chip>
        <Chip
          active={filters.state.sortField === 'project_key'}
          onClick={() => filters.setSortField('project_key')}
        >
          {t('v2.staffing.sort_project_key', 'Projektnummer')}
        </Chip>
        <button
          type="button"
          onClick={filters.toggleSortDir}
          aria-label={
            filters.state.sortDir === 'asc'
              ? t('v2.staffing.sort_asc', 'Aufsteigend')
              : t('v2.staffing.sort_desc', 'Absteigend')
          }
          title={
            filters.state.sortDir === 'asc'
              ? t('v2.staffing.sort_asc', 'Aufsteigend')
              : t('v2.staffing.sort_desc', 'Absteigend')
          }
          className="px-2.5 py-1 rounded-md text-xs transition-colors"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-2)',
            border: '1px solid var(--line-soft)',
            fontFamily: 'var(--f-mono)',
          }}
        >
          {filters.state.sortDir === 'asc' ? '▲' : '▼'}
        </button>
      </ChipRow>
    </div>
  );
}
